import React from "react";
import { Container, Col, Row, Card } from "react-bootstrap";
import { motion } from "framer-motion";
import tip1 from "../assets/optTip1.jpg";
import tip2 from "../assets/opttip4.jpg";
import tip3 from "../assets/optTip3.jpg";
import tip4 from "../assets/optTip4.png";
import tip5 from "../assets/optTip5.jpg";

const fadeIn = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

const slideInLeft = {
  hidden: { opacity: 0, x: -50 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.7 } },
};

const slideInRight = {
  hidden: { opacity: 0, x: 50 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.7 } },
};

// Investment tips content
const investingTips = [
  {
    title: "Assess Your Financial Situation",
    content:
      "Before you begin investing, take time to evaluate your financial standing. Review your savings, income, expenses, and any debts to understand how much you can afford to invest. A common myth is that you need a large sum to get started, but that's not the case. Some investment options allow you to begin with as little as $500.",
    image: tip1,
  },
  {
    title: "Set Clear Goals",
    content:
      "Defining your financial goals is crucial for long-term success. Without a clear plan, it's easy to get caught up in market fluctuations and short-term trends. Write down your goals for the short, medium, and long term. Keeping them in focus will help you stick to your investment strategy and avoid impulsive decisions.",
    image: tip2,
  },
  {
    title: "Diversify Your Investments",
    content:
      "Spreading your investments across different asset classes—such as stocks, bonds, real estate, and cash—can help manage risk and provide more stable returns. Diversification ensures that if one sector or company underperforms, your overall portfolio remains balanced. A well-diversified strategy reduces risk while still offering growth potential.",
    image: tip3,
  },
  {
    title: "Educate Yourself",
    content:
      "Many investors rely on advice from friends, family, or social media, but it's important to do your own research. Consider learning from credible sources such as financial podcasts, investment company websites, and educational seminars. If needed, consult a financial advisor for professional guidance.",
    image: tip4,
  },
  {
    title: "Stay Disciplined and Focused",
    content:
      "It's easy to get tempted by unnecessary spending, but maintaining financial discipline is key. Creating a realistic budget that accounts for your lifestyle helps keep you on track. Most importantly, stay committed to your investment goals. Keeping your end objective in mind will motivate you to remain consistent and make informed financial decisions.",
    image: tip5,
  },
];

const Tips = () => {
  const professionalStyles = {
    heroSection: {
      background: "linear-gradient(135deg, #001f3f 0%, #003366 100%)",
      padding: "100px 50px",
      marginBottom: "80px",
    },
    heroTitle: {
      fontSize: "3.5rem",
      fontWeight: "700",
      letterSpacing: "-0.5px",
      marginBottom: "25px",
      color: "#fff",
      textTransform: "uppercase",
      textAlign: "center",
    },
    heroText: {
      fontSize: "1.2rem",
      maxWidth: "800px",
      margin: "0 auto",
      lineHeight: "1.8",
      fontWeight: "300",
      color: "#fff",
      textAlign: "center",
    },
    tipCard: {
      border: "none",
      borderLeft: "4px solid #003366",
      background: "#fff",
      boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
      borderRadius: "0",
      padding: "0",
      overflow: "hidden",
      marginBottom: "40px",
    },
    tipNumber: {
      display: "inline-block",
      width: "50px",
      height: "50px",
      background: "#003366",
      color: "#fff",
      fontSize: "1.5rem",
      fontWeight: "700",
      textAlign: "center",
      lineHeight: "50px",
      marginBottom: "20px",
    },
    tipTitle: {
      fontSize: "1.8rem",
      fontWeight: "700",
      color: "#003366",
      marginBottom: "20px",
      textTransform: "uppercase",
      letterSpacing: "-0.5px",
    },
    tipContent: {
      fontSize: "1.05rem",
      lineHeight: "1.9",
      color: "#555",
      textAlign: "justify",
    },
    tipImage: {
      width: "100%",
      height: "350px",
      objectFit: "cover",
      borderRadius: "0",
    },
    contentPadding: {
      padding: "40px",
    },
    callToAction: {
      background: "#f8f9fa",
      padding: "60px 40px",
      marginTop: "60px",
      marginBottom: "80px",
      borderLeft: "4px solid #003366",
      boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
      textAlign: "center",
    },
    ctaTitle: {
      fontSize: "2rem",
      fontWeight: "700",
      color: "#003366",
      marginBottom: "20px",
      textTransform: "uppercase",
      letterSpacing: "-0.5px",
    },
    ctaText: {
      fontSize: "1.1rem",
      lineHeight: "1.8",
      color: "#555",
      marginBottom: "30px",
      maxWidth: "700px",
      margin: "0 auto 30px",
    },
    ctaButton: {
      display: "inline-block",
      background: "#003366",
      color: "#fff",
      padding: "15px 45px",
      fontSize: "1.1rem",
      fontWeight: "600",
      letterSpacing: "0.5px",
      textTransform: "uppercase",
      textDecoration: "none",
      border: "2px solid #003366",
      transition: "all 0.3s ease",
    },
  };

  return (
    <Container fluid style={{ padding: 0 }}>
      {/* Hero Section */}
      <motion.div
        style={professionalStyles.heroSection}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6 }}
      >
        <motion.h1
          style={professionalStyles.heroTitle}
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.8 }}
        >
          Investment Tips
        </motion.h1>
        <motion.p
          style={professionalStyles.heroText}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4, duration: 0.8 }}
        >
          Starting your investment journey? Here are five essential tips to help
          you build a solid foundation and achieve your financial goals with
          confidence.
        </motion.p>
      </motion.div>

      {/* Tips Content Section */}
      <Container style={{ paddingBottom: "40px" }}>
        {investingTips.map((tip, index) => (
          <motion.div
            key={index}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            variants={fadeIn}
          >
            <Card style={professionalStyles.tipCard}>
              <Row className="g-0">
                {/* Alternate layout: even index - image left, odd index - image right */}
                {index % 2 === 0 ? (
                  <>
                    <Col md={5}>
                      <motion.img
                        src={tip.image}
                        alt={tip.title}
                        style={professionalStyles.tipImage}
                        variants={slideInLeft}
                        whileHover={{
                          scale: 1.05,
                          transition: { duration: 0.3 },
                        }}
                      />
                    </Col>
                    <Col md={7}>
                      <motion.div
                        style={professionalStyles.contentPadding}
                        variants={slideInRight}
                      >
                        <div style={professionalStyles.tipNumber}>
                          {index + 1}
                        </div>
                        <h3 style={professionalStyles.tipTitle}>{tip.title}</h3>
                        <p style={professionalStyles.tipContent}>
                          {tip.content}
                        </p>
                      </motion.div>
                    </Col>
                  </>
                ) : (
                  <>
                    <Col md={7} className="order-md-1 order-2">
                      <motion.div
                        style={professionalStyles.contentPadding}
                        variants={slideInLeft}
                      >
                        <div style={professionalStyles.tipNumber}>
                          {index + 1}
                        </div>
                        <h3 style={professionalStyles.tipTitle}>{tip.title}</h3>
                        <p style={professionalStyles.tipContent}>
                          {tip.content}
                        </p>
                      </motion.div>
                    </Col>
                    <Col md={5} className="order-md-2 order-1">
                      <motion.img
                        src={tip.image}
                        alt={tip.title}
                        style={professionalStyles.tipImage}
                        variants={slideInRight}
                        whileHover={{
                          scale: 1.05,
                          transition: { duration: 0.3 },
                        }}
                      />
                    </Col>
                  </>
                )}
              </Row>
            </Card>
          </motion.div>
        ))}

        {/* Call to Action Section */}
        <motion.div
          style={professionalStyles.callToAction}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 style={professionalStyles.ctaTitle}>Ready to Get Started?</h2>
          <p style={professionalStyles.ctaText}>
            Take the first step towards achieving your investment goals. Our
            expert team is ready to guide you through every stage of your
            investment journey with personalized strategies and professional
            support.
          </p>
          <a
            href="/contact"
            style={professionalStyles.ctaButton}
            onMouseEnter={(e) => {
              e.target.style.background = "#fff";
              e.target.style.color = "#003366";
            }}
            onMouseLeave={(e) => {
              e.target.style.background = "#003366";
              e.target.style.color = "#fff";
            }}
          >
            Contact Us Today
          </a>
        </motion.div>
      </Container>
    </Container>
  );
};

export default Tips;
